import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAuth } from "../AuthProvider";

const DEFAULT_SETTINGS = {
  price: 5000,
  durationDays: 7,
  maxSlots: 5,
  note:
    "Featured sponsored shops appear first on the homepage and stay highlighted for the selected period.",
};

const fmt = (value) =>
  `LKR ${Number(value || 0).toLocaleString("en-LK", {
    maximumFractionDigits: 0,
  })}`;

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value) {
  const ms = toMillis(value);
  return ms ? new Date(ms).toLocaleString() : "Not set";
}

export default function StoreSponsorships() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [storeDoc, setStoreDoc] = useState(null);
  const [activeSponsor, setActiveSponsor] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [slip, setSlip] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "sponsorship_settings", "defaults"),
      (snap) => {
        setSettings({ ...DEFAULT_SETTINGS, ...(snap.data() || {}) });
      },
      (settingsError) => {
        console.error("sponsorship settings load failed", settingsError);
      },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return onSnapshot(doc(db, "stores", user.uid), (snap) => {
      setStoreDoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return onSnapshot(doc(db, "store_sponsorships", user.uid), (snap) => {
      const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      setActiveSponsor(data && toMillis(data.endsAt) > Date.now() ? data : null);
    });
  }, [user?.uid]);

  useEffect(() => {
    const q = query(
      collection(db, "bank_accounts"),
      where("isActive", "==", true),
    );
    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((row) => ({ id: row.id, ...row.data() }))
          .sort(
            (a, b) =>
              Number(a.displayOrder ?? 100) - Number(b.displayOrder ?? 100),
          );
        setBankAccounts(rows);
      },
      (bankError) => {
        console.error("sponsorship bank account fetch failed", bankError);
        setBankAccounts([]);
      },
    );
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const q = query(
      collection(db, "store_sponsorship_orders"),
      where("instructorId", "==", user.uid),
    );
    return onSnapshot(
      q,
      (snap) => {
        setOrders(
          snap.docs
            .map((row) => ({ id: row.id, ...row.data() }))
            .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt)),
        );
      },
      (ordersError) => {
        console.error("sponsorship order fetch failed", ordersError);
      },
    );
  }, [user?.uid]);

  const pendingOrder = useMemo(
    () =>
      orders.find((row) =>
        ["pending", "submitted", "initiated"].includes(
          String(row.status || "").toLowerCase(),
        ),
      ) || null,
    [orders],
  );

  const storeReady = !!storeDoc?.published && !!storeDoc?.slug;
  const canSubmit = storeReady && !activeSponsor && !pendingOrder;

  async function handleSubmit() {
    if (!user?.uid || !canSubmit) return;
    if (!slip) {
      setError("Upload your sponsorship payment slip first.");
      return;
    }

    setSubmitting(true);
    setMessage("");
    setError("");

    try {
      const orderRef = doc(collection(db, "store_sponsorship_orders"));
      const ext = (slip.name || "").split(".").pop() || "jpg";
      const storageRef = ref(
        storage,
        `uploads/store_sponsorships/${user.uid}/${orderRef.id}/slip.${ext}`,
      );
      await uploadBytes(storageRef, slip);
      const slipUrl = await getDownloadURL(storageRef);

      await setDoc(orderRef, {
        instructorId: user.uid,
        storeId: user.uid,
        storeSnapshot: {
          displayName: storeDoc?.displayName || "",
          slug: storeDoc?.slug || "",
          profileImageUrl: storeDoc?.profileImageUrl || "",
          coverImageUrl: storeDoc?.coverImageUrl || "",
          categories: Array.isArray(storeDoc?.categories) ? storeDoc.categories : [],
          bio: storeDoc?.bio || "",
        },
        instructorSnapshot: {
          name: storeDoc?.displayName || user.displayName || user.email || user.uid,
          email: user.email || "",
        },
        price: Number(settings.price || DEFAULT_SETTINGS.price),
        durationDays: Number(
          settings.durationDays || DEFAULT_SETTINGS.durationDays,
        ),
        status: "pending",
        slipUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSlip(null);
      setMessage(
        "Sponsorship slip submitted. Admin will review it and place your shop in the homepage spotlight.",
      );
    } catch (submitError) {
      console.error(submitError);
      setError("Failed to submit your sponsorship request.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <div className="rounded-[32px] border border-amber-200 bg-gradient-to-br from-[#fff6e6] via-white to-[#fff0dc] p-6 shadow-[0_18px_45px_-24px_rgba(123,75,17,0.35)]">
        <div className="text-sm font-semibold uppercase tracking-[0.3em] text-amber-700">
          Store Promotion
        </div>
        <h1 className="mt-2 text-3xl font-black text-slate-900">
          Sponsor your shop on the homepage
        </h1>
        <p className="mt-3 max-w-3xl text-sm text-slate-600 sm:text-base">
          Put your store in the premium spotlight at the top of the luxury mall
          landing page so more buyers discover it first.
        </p>
      </div>

      {(message || error) && (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            error
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {error || message}
        </div>
      )}

      {activeSponsor ? (
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-700">
                Live Sponsor
              </div>
              <div className="mt-2 text-2xl font-bold text-slate-900">
                Your shop is currently featured on the homepage
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Runs until {formatDate(activeSponsor.endsAt)}
              </div>
            </div>
            {storeDoc?.slug ? (
              <Link
                to={`/store/${storeDoc.slug}`}
                className="rounded-xl border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
              >
                Open My Store
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {!activeSponsor && pendingOrder ? (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
          <div className="text-sm font-semibold uppercase tracking-[0.24em] text-amber-700">
            Pending Review
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900">
            Your sponsorship request is waiting for admin approval
          </div>
          <div className="mt-2 text-sm text-slate-600">
            Submitted on {formatDate(pendingOrder.createdAt)}. Once approved,
            your shop will move into the homepage sponsored section
            automatically.
          </div>
          {pendingOrder.slipUrl ? (
            <a
              href={pendingOrder.slipUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-white"
            >
              View uploaded slip
            </a>
          ) : null}
        </div>
      ) : null}

      {!storeReady ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-xl font-bold text-slate-900">
            Finish your store first
          </div>
          <div className="mt-2 max-w-3xl text-sm text-slate-600">
            To sponsor your shop, first save and publish your store so buyers
            can actually open it from the homepage spotlight.
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/dashboard"
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Open Dashboard
            </Link>
            <Link
              to="/store"
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-slate-50"
            >
              Complete Store Setup
            </Link>
          </div>
        </div>
      ) : null}

      {!activeSponsor && !pendingOrder && storeReady ? (
        <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr]">
          <section className="rounded-[32px] border border-[#ead8b8] bg-white p-6 shadow-[0_18px_45px_-24px_rgba(123,75,17,0.3)]">
            <div className="rounded-[28px] bg-gradient-to-br from-[#24162f] via-[#231a45] to-[#8133bc] p-6 text-white shadow-xl">
              <div className="text-sm font-semibold uppercase tracking-[0.28em] text-[#f3d08c]">
                Homepage Spotlight
              </div>
              <div className="mt-4 text-4xl font-black">
                {fmt(settings.price)}
              </div>
              <div className="mt-2 text-lg text-white/85">
                {Number(settings.durationDays)} day sponsored placement
              </div>
              <div className="mt-5 rounded-2xl bg-white/10 p-4 text-sm text-white/85">
                {settings.note || DEFAULT_SETTINGS.note}
              </div>
            </div>

            <div className="mt-5 space-y-3 rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-lg font-bold text-slate-900">How it works</div>
              <ol className="space-y-3 text-sm text-slate-600">
                <li>1. Transfer the sponsor amount to the admin bank account.</li>
                <li>2. Upload your slip once and wait for review.</li>
                <li>3. After admin approval, your store goes to the homepage spotlight.</li>
              </ol>
            </div>

            <div className="mt-5 rounded-3xl border border-slate-200 bg-[#fff8ed] p-5">
              <div className="text-lg font-bold text-slate-900">Admin bank details</div>
              <div className="mt-4 space-y-3">
                {bankAccounts.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                    No active bank accounts are available right now. Please
                    contact admin.
                  </div>
                ) : (
                  bankAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="rounded-2xl border border-white bg-white px-4 py-4 text-sm text-slate-700 shadow-sm"
                    >
                      <div className="font-bold text-slate-900">
                        {account.bankName || "Bank account"}
                      </div>
                      <div className="mt-1">
                        Account holder: {account.accountHolder || "-"}
                      </div>
                      <div>Account number: {account.accountNumber || "-"}</div>
                      <div>Branch: {account.branch || "-"}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          <section className="rounded-[32px] border border-[#ead8b8] bg-white p-6 shadow-[0_18px_45px_-24px_rgba(123,75,17,0.3)]">
            <div className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">
              Submit Request
            </div>
            <div className="mt-2 text-2xl font-black text-slate-900">
              Upload your sponsor payment slip
            </div>
            <div className="mt-2 text-sm text-slate-600">
              Your store will stay featured for {Number(settings.durationDays)} days
              after admin approval.
            </div>

            <label className="mt-6 flex cursor-pointer flex-col items-center justify-center rounded-[28px] border-2 border-dashed border-blue-200 bg-blue-50/70 px-5 py-10 text-center">
              <div className="text-lg font-semibold text-slate-900">
                {slip ? slip.name : "Choose sponsorship slip"}
              </div>
              <div className="mt-2 text-sm text-slate-500">
                JPG, PNG, WEBP, or PDF up to 10MB
              </div>
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(event) => setSlip(event.target.files?.[0] || null)}
              />
            </label>

            <button
              type="button"
              disabled={!slip || submitting}
              onClick={handleSubmit}
              className={`mt-6 w-full rounded-2xl px-5 py-3 text-base font-bold text-white ${
                !slip || submitting
                  ? "bg-slate-300"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {submitting ? "Submitting..." : "Submit sponsorship request"}
            </button>
          </section>
        </div>
      ) : null}
    </div>
  );
}
